import { useState, useCallback } from 'react';

export type CustomValidator = (
  field: string,
  value: string,
  setInvalidField: (identifier: string) => void,
  removeInvalidField: (identifier: string) => void,
) => void;

export const useFieldValidation = (validators?: CustomValidator[]) => {
  const [invalidFields, setInvalidField] = useState<string[]>([]);

  const removeInvalidField = useCallback((field: string) => {
    setInvalidField(prev => prev.filter(f => field !== f));
  }, []);

  const removeInvalidFields = useCallback((fields: string[]) => {
    setInvalidField(prev => prev.filter(f => !fields.includes(f)));
  }, []);

  const validateField = useCallback(
    (field: string, value: string, required = true) => {
      if (required && !value?.length && !invalidFields.includes(field)) {
        setInvalidField([...invalidFields, field]);
        return;
      }

      if (validators && validators.length > 0) {
        validators.forEach((validator: CustomValidator) =>
          validator(
            field,
            value,
            (identifier: string) =>
              !invalidFields.includes(identifier) && setInvalidField([...invalidFields, identifier]),
            (identifier: string) => removeInvalidField(identifier),
          ),
        );
        return;
      }

      if (value.length > 0 && invalidFields.includes(field)) {
        removeInvalidField(field);
        return;
      }
    },
    [invalidFields, removeInvalidField, validators],
  );

  return {
    invalidFields,
    setInvalidField,
    validateField,
    removeInvalidField,
    removeInvalidFields,
  };
};
